import { useState, useEffect, useCallback } from 'react';
import type { PublishedVersion } from '../types';
import { fetchPublishedVersion } from '../lib/api';

const REFRESH_MS = 60_000;

/**
 * Load only the published schedule snapshot (no login required).
 */
export function usePublishedBoard(eventId: string | null) {
  const [publishedVersion, setPublishedVersion] = useState<PublishedVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) {
      setIsLoading(false);
      return;
    }

    try {
      const published = await fetchPublishedVersion(eventId);
      setPublishedVersion(published);
      setError(null);
    } catch (err) {
      console.error('Failed to load published board:', err);
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setIsLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (!eventId) return;
    const id = window.setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [eventId, load]);

  return { publishedVersion, isLoading, error, refresh: load };
}
